import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import WorldMap from "./WorldMap";
import { API_BASE_URL } from "../utils/config";

const HomePage = () => {
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  console.log("HomePage rendered");

  // Fetch available locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);

  // Fetch available locations
  const fetchLocations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/available-locations`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      setError("Failed to load locations. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to normalize text for better searching
  const normalizeSearchText = (text) => {
    return text
      .toLowerCase()
      .replace(/[.,\-_\s]+/g, "") // Remove dots, commas, dashes, underscores, and spaces
      .replace(/[^\w]/g, ""); // Remove any remaining non-word characters
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const normalizedSearchTerm = normalizeSearchText(searchTerm);

    const foundLocation = locations.find((location) => {
      const normalizedTitle = normalizeSearchText(location.title);
      return normalizedTitle.includes(normalizedSearchTerm);
    });

    if (foundLocation) {
      navigate("/macroclimate", { state: { selectedLocation: foundLocation } });
    } else {
      setError("Location not found. Please try another search term.");
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Only show suggestions when there's a search term
    if (value.trim().length > 0) {
      const normalizedSearchTerm = normalizeSearchText(value);

      const filtered = locations.filter((location) => {
        const normalizedTitle = normalizeSearchText(location.title);
        return normalizedTitle.includes(normalizedSearchTerm);
      });

      // Sort by relevance - exact matches first, then partial matches
      filtered.sort((a, b) => {
        const normalizedA = normalizeSearchText(a.title);
        const normalizedB = normalizeSearchText(b.title);

        // Check if either is an exact match
        const aExact = normalizedA === normalizedSearchTerm;
        const bExact = normalizedB === normalizedSearchTerm;

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Check if either starts with the search term
        const aStarts = normalizedA.startsWith(normalizedSearchTerm);
        const bStarts = normalizedB.startsWith(normalizedSearchTerm);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        // Alphabetical order for remaining items
        return a.title.localeCompare(b.title);
      });

      setSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (location) => {
    setSearchTerm(location.title);
    setShowSuggestions(false);
    navigate("/macroclimate", { state: { selectedLocation: location } });
  };

  const handleLocationClick = (location) => {
    navigate("/macroclimate", { state: { selectedLocation: location } });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 py-5">
        <div className="flex justify-between items-center px-14">
          <Link
            to="/"
            className="text-2xl font-bold no-underline flex items-center"
          >
            <img
              src="/heading-icon-heavy.png"
              alt="ClimaPro.AI"
              className="h-10"
            />
          </Link>
          <nav className="space-x-8">
            <Link
              to="/contact"
              className="text-sm font-bold text-gray-600 hover:text-gray-900"
            >
              CONTACT
            </Link>
            <Link
              to="/about"
              className="text-sm font-bold text-gray-600 hover:text-gray-900"
            >
              ABOUT
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row flex-1">
        {/* Left Column - Text Content */}
        <div className="md:w-1/2 p-14 flex flex-col justify-between">
          <div>
            <h2 className="text-3xl font-semibold leading-tight mb-4 text-left">
              Your Climate Data AI Interpreter.
            </h2>

            <p className="text-gray-600 mt-6 mb-8">
              Access real-time climate data analysis powered by AI to inform
              your architectural and engineering decisions. ClimaPro.AI provides
              comprehensive insights into temperature patterns, solar exposure,
              and environmental conditions worldwide.
            </p>
          </div>

          {/* Search Input */}
          <div className="mt-auto relative">
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.id || suggestion.url}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.title}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center border-t border-b border-gray-300 py-4">
              <input
                type="text"
                placeholder="Input City Name..."
                value={searchTerm}
                onChange={handleInputChange}
                className="flex-grow text-xl focus:outline-none"
              />
              <button
                onClick={handleSearch}
                className="ml-2 bg-gray-800 text-white rounded-full w-8 h-8 flex items-center justify-center"
              >
                <span>➜</span>
              </button>
            </div>

            {error && <p className="mt-2 text-red-500">{error}</p>}
          </div>
        </div>

        {/* Right Column - Map */}
        <div className="md:w-2/3 relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800"></div>
              <span className="ml-3 text-gray-600">Loading locations...</span>
            </div>
          ) : (
            <div className="h-full">
              <WorldMap
                locations={locations}
                onLocationClick={handleLocationClick}
                searchTerm={searchTerm}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
